/**
 * Phase 126.B — Classroom Support Hub: Twice-Exceptional (2e) page.
 *
 * Public, no-login, shareable URL. The 2e page is the most important
 * one in the Hub — masking is what makes these students invisible for
 * years. Per the spec it must cross-link to BOTH generators and
 * include the optional "this describes adults too" note for the
 * teachers reading who recognize themselves.
 */
import ProfilePageLayout from "../../components/classroom-support/ProfilePageLayout";

export default function TwiceExceptional() {
  return (
    <ProfilePageLayout
      slug="2e"
      metaTitle="Twice-Exceptional (2e) in the Classroom — Atomic Edu"
      metaDescription="Bright and struggling at the same time. A teacher-to-teacher guide for spotting 2e students — the kids who are gifted and have ADHD (or another exceptionality) — and the both-at-once approach that works."
      hero={{
        title: "Twice-Exceptional (2e)",
        tagline:
          "Gifted AND ADHD (or another exceptionality). The defining feature is masking — the gift hides the challenge and the challenge hides the gift, so the student often reads as 'average' or 'inconsistent' and goes unidentified for years.",
      }}
      sections={[
        {
          id: "looks_like",
          body:
            "2e is the bright student who can't get organized. The one whose ideas in conversation don't show up in writing. The one with the report-card phrase 'not working to potential'. You might notice:",
          bullets: [
            "Uneven performance — brilliant on the test, blank on the homework.",
            "Strong oral ideas, weak written output.",
            "Frustration with their own work that feels disproportionate.",
            "Years of teachers who said 'capable, just needs to focus / try harder / be more organized'.",
            "Asynchrony writ large — they can argue an abstract idea and still lose their pencil twice in one period.",
          ],
        },
        {
          id: "affects",
          body:
            "The two profiles cancel out — and that's the problem. The strength hides the support need, and the support need hides the strength. Neither gets addressed.",
          bullets: [
            "Teachers see 'average' and stop looking.",
            "Enrichment is withheld because output looks inconsistent.",
            "Support is withheld because ideas sound advanced.",
            "Frustration builds quietly — and then loudly — when neither side of who they are is named.",
          ],
        },
        {
          id: "strengths",
          body:
            "The same advanced thinking gifted peers bring — plus a layer of creativity built from a lifetime of working around the challenge. 2e students are often the most resourceful problem-solvers in the room.",
          bullets: [
            "Advanced reasoning and capacity for complexity.",
            "Creative workarounds — they've been engineering them since Grade 1.",
            "A strong, often quiet sense of fairness — they know what it's like to be misread.",
            "Unusual angles on familiar problems.",
          ],
        },
        {
          id: "thrives",
          body:
            "This is the non-negotiable for 2e: both at once. Enrichment that feeds the strength AND scaffolding that supports the access need. You cannot pick one.",
          bullets: [
            "Depth and structure together — a higher-order question delivered in chunks, with a visible timer.",
            "Choice of output AND a clear start cue.",
            "Real audience AND a self-monitoring checklist.",
            "Curriculum compacting AND a movement break.",
          ],
        },
        {
          id: "strategies",
          body:
            "Run both generators. The ADHD support generator gives you the scaffolding side; the gifted extension generator gives you the depth side. Together they describe a learner who is held to grade-level thinking AND given the supports they need to show it.",
        },
      ]}
      ctas={undefined}
      generators={[
        {
          to: "/classroom-support/adhd/generator",
          title: "ADHD support plan",
          subtitle:
            "The access-need side. Chunks, timers, choice of output, the 4-to-1 ratio, the start cue.",
          cta: "Generate a plan for ADHD",
        },
        {
          to: "/classroom-support/giftedness/generator",
          title: "Gifted extension plan",
          subtitle:
            "The strength side. Higher-order questions, metacognition, passion projects, productive-failure framing.",
          cta: "Generate a plan for Giftedness",
        },
      ]}
      readNext={[
        {
          to: "/classroom-support/adhd",
          title: "ADHD in the Classroom",
          subtitle:
            "The access-need profile in detail — movement, chunks, immediate feedback.",
          cta: "Read the guide",
        },
        {
          to: "/classroom-support/giftedness",
          title: "Giftedness in the Classroom",
          subtitle:
            "The strength profile in detail — depth over volume, productive-failure framing.",
          cta: "Read the guide",
        },
      ]}
      adultNote="If you read this page and recognized yourself — you are not alone. 2e describes adults too. Many people went their entire school career unidentified because the gift and the challenge cancelled each other out. Naming it now changes nothing about who you've been and everything about how you read your own story."
    />
  );
}
